import { Component } from '@theme/component';

/**
 * @typedef {Object} ReadMoreTextRefs
 * @property {HTMLElement} content - Truncated description content
 * @property {HTMLButtonElement} toggle - Read more / Read less control
 * @property {HTMLElement} [expandLabel] - Label shown while collapsed
 * @property {HTMLElement} [collapseLabel] - Label shown while expanded
 */

/**
 * Truncates product (or other) rich text and reveals it with a Read more control.
 *
 * @extends {Component<ReadMoreTextRefs>}
 */
class ReadMoreText extends Component {
  requiredRefs = ['content', 'toggle'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /**
   * Width the copy was last measured at. The clamp we apply changes the
   * content's HEIGHT, never its width, so ignoring same-width notifications
   * filters out the observer echo caused by our own mutation.
   * @type {number | null}
   */
  #measuredWidth = null;

  /** Re-entrancy guard for the brief unclamped moment inside #evaluate. */
  #measuring = false;

  connectedCallback() {
    super.connectedCallback();
    this.#evaluate();

    if (typeof ResizeObserver === 'function') {
      this.#resizeObserver = new ResizeObserver(() => this.#evaluate());
      this.#resizeObserver.observe(this.refs.content);
    }
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  /**
   * Collapses overflowing copy unless the shopper has already expanded it.
   */
  #evaluate = () => {
    if (this.hasAttribute('expanded')) return;
    if (this.#measuring) return;

    const { content, toggle } = this.refs;

    // Only a width change can alter how many lines the copy wraps to. Bailing
    // out on same-width notifications stops the ResizeObserver from reacting to
    // the height change that this method itself causes.
    const width = Math.round(content.getBoundingClientRect().width);
    if (width === this.#measuredWidth) return;
    this.#measuredWidth = width;

    this.#measuring = true;

    // The clamp is `-webkit-line-clamp`, and with it applied `scrollHeight`
    // reports the CLAMPED height rather than the full copy. Measuring while
    // clamped therefore always concludes the text fits, which removes the
    // clamp, which makes it overflow again — the flicker. Measure unclamped.
    const wasClamped = this.dataset.ready === 'true';
    if (wasClamped) delete this.dataset.ready;

    const sample = content.querySelector('p, li') || content;
    const lineCount = Number(this.dataset.lines || 5);
    const computed = getComputedStyle(sample);
    const parsedLineHeight = Number.parseFloat(computed.lineHeight);
    const lineHeight = Number.isFinite(parsedLineHeight)
      ? parsedLineHeight
      : Number.parseFloat(computed.fontSize) * 1.6;
    const maxHeight = lineHeight * lineCount;
    const overflows = content.scrollHeight > maxHeight + 2;

    if (overflows) this.dataset.ready = 'true';
    toggle.hidden = !overflows;

    // Hold the guard until the resize caused by re-clamping has been delivered,
    // otherwise that notification re-enters before #measuredWidth can absorb it.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#measuring = false;
      });
    });
  };

  /**
   * Expands or collapses the truncated text.
   */
  toggleExpanded = () => {
    const expanded = this.hasAttribute('expanded');
    this.toggleAttribute('expanded', !expanded);
    this.refs.toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');

    const { expandLabel, collapseLabel } = this.refs;
    if (expandLabel) expandLabel.hidden = !expanded;
    if (collapseLabel) collapseLabel.hidden = expanded;

    if (expanded) {
      // Collapsing back: the width has not changed, so clear the cached
      // measurement or #evaluate would bail out and never re-apply the clamp.
      this.#measuredWidth = null;
      this.#evaluate();
    }
  };
}

if (!customElements.get('read-more-text')) {
  customElements.define('read-more-text', ReadMoreText);
}
